import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { convertImageUrlsToFiles, pinImagesToIPFS } from "../../../../Components/functions/IPFS";
import { api, mintProperty } from "../../../../Components/functions/Api";
import DraftService from "../../../../Services/DraftService";
import IcalSyncService from "../../../../Services/IcalSyncService";
import { PurpleButton } from "../../../../Components/Buttons/PurpleButton";
import { upsertMintJob } from "../../../../ReduxSlices/MintSlice";
import imageCompression from "browser-image-compression";
import { NibiruTxClient } from "@nibiruchain/nibijs";
import { Mainnet } from "@nibiruchain/nibijs";
import { getRoleSpecificVerification } from "../../../../utils/verificationUtils";
import { executeRentalWithWallet } from "../../../../Components/functions/Contract";
import { evmToBech32Address } from "../../../../evm/services/EvmContract";

// Import all Mint pages
import MintWelcomePage from "./pages/MintWelcomePage";
import MintBasicInfoPage from "./pages/MintBasicInfoPage";
import MintEssentialsPage from "./pages/MintEssentialsPage";
import MintPhotosPage from "./pages/MintPhotosPage";
import MintPublishPage from "./pages/MintPublishPage";
import MintAmenitiesPage from "./pages/MintAmenitiesPage";

const normalizeAirbnbAmenities = (amenitiesDetailed, fallbackAmenities) => {
  const isObjectMap =
    fallbackAmenities &&
    typeof fallbackAmenities === "object" &&
    !Array.isArray(fallbackAmenities);

  if (isObjectMap) {
    return fallbackAmenities;
  }

  const normalized = {};

  if (Array.isArray(amenitiesDetailed) && amenitiesDetailed.length > 0) {
    amenitiesDetailed.forEach((section) => {
      if (!section || section.available === false) return;
      const category = section.category?.trim() || "Others";
      const items = Array.isArray(section.items) ? section.items : [];
      const availableItems = items
        .filter((item) => item && item.available !== false && item.name)
        .map((item) => item.name.trim())
        .filter(Boolean);

      if (availableItems.length === 0) return;

      normalized[category] = Array.from(
        new Set([...(normalized[category] || []), ...availableItems])
      );
    });
  }

  if (Object.keys(normalized).length > 0) {
    return normalized;
  }

  if (Array.isArray(fallbackAmenities) && fallbackAmenities.length > 0) {
    return {
      Others: Array.from(new Set(fallbackAmenities.filter(Boolean))),
    };
  }

  return {};
};

const MintMain = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const account = useSelector((state) => state.auth.account);
  const wallet = useSelector((state) => state.auth.wallet);
  const profile = useSelector((state) => state.auth.profile);
  
  // Draft state
  const [draftId, setDraftId] = useState(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  
  // Airbnb blocked dates (scraped during import, will be blocked after mint)
  const [airbnbBlockedDates, setAirbnbBlockedDates] = useState(null);

  // Page navigation state
  const [pageIdx, setPageIdx] = useState(0);

  // Map and location state
  const [zoom, setZoom] = useState(10);
  const [pinLocation, setPinLocation] = useState({
    lat: 25,
    lng: 55,
  });
  const [addressInput, setAddressInput] = useState();
  const [predictList, setPredictList] = useState([]);
  const [addressDetails, setAddressDetails] = useState(null);

  // Google Maps services - memoized to prevent recreation on every render
  const service = useMemo(
    () => window.google?.maps?.places?.AutocompleteService 
      ? new window.google.maps.places.AutocompleteService() 
      : null,
    []
  );

  const geocoder = useMemo(
    () => window.google?.maps?.Geocoder 
      ? new window.google.maps.Geocoder() 
      : null,
    []
  );

  // Image state
  const [images, setImages] = useState([]);
  const [dragStartIdx, setDragStartIdx] = useState(0);
  const [dragEndIdx, setDragEndIdx] = useState(0);

  // Modal states
  const [isOpen, setIsOpen] = useState(false);
  const [checkInModal, setCheckInModal] = useState(false);
  const [checkOutModal, setCheckOutModal] = useState(false);

  // Generate random property ID
  function generateRandom8DigitInteger() {
    return Math.floor(10000000 + Math.random() * 90000000);
  }

  // Metadata state
  const [metaDetails, setMetaDetails] = useState({
    buildingName: "",
    propertyId: generateRandom8DigitInteger().toString(),
    groundRule: "",
    checkIn: "10:00",
    checkOut: "10:00",
    wifiPass: "",
    description: "",
    cancellation: "",
    amenities: {},
    pets: false,
    essentials: {
      guests: 1,
      bedrooms: 1,
      beds: 1,
      bathrooms: 1,
      square: 50,
    },
  });

  const propertyId = metaDetails?.propertyId;
  // Memoize Redux selector to prevent unnecessary re-renders
  const mintJob = useSelector(
    (state) => (propertyId ? state.mint.jobs[propertyId] : null),
    (prev, next) => {
      // Custom equality check - only re-render if mintJob actually changed
      if (!prev && !next) return true; // Both null, equal
      if (!prev || !next) return false; // One null, not equal
      // Compare relevant fields
      return (
        prev.sync_status === next.sync_status &&
        prev.txHash === next.txHash &&
        prev.error === next.error
      );
    }
  );
  const [isMintSubmitting, setIsMintSubmitting] = useState(false);

  const mintStatusMessage = useMemo(() => {
    if (!mintJob) return null;
    switch (mintJob.sync_status) {
      case "confirmed":
        return "Mint confirmed on-chain! Your property is now live.";
      case "failed":
        return `Mint failed${mintJob.error ? `: ${mintJob.error}` : ""}. Please review and try again.`;
      case "reconciling":
        return "Mint is reconciling with on-chain state. We will update you shortly.";
      default:
        return "Mint request queued. Waiting for blockchain confirmation...";
    }
  }, [mintJob]);

  const mintStatusTone = useMemo(() => {
    if (!mintJob) return null;
    switch (mintJob.sync_status) {
      case "confirmed":
        return {
          bg: "bg-[#ECFDF5]",
          border: "border-[#A7F3D0]",
          text: "text-[#047857]",
        };
      case "failed":
        return {
          bg: "bg-[#FEF2F2]",
          border: "border-[#FECACA]",
          text: "text-[#B91C1C]",
        };
      default:
        return {
          bg: "bg-[#EEF2FF]",
          border: "border-[#C7D2FE]",
          text: "text-[#3730A3]",
        };
    }
  }, [mintJob]);

  const fileToDataUrl = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Upload images to Pinata and return IPFS URLs
  const prepareMediaForMint = useCallback(
    async (sourceImages) => {
      const MAX_ALLOWABLE_IMAGES = 20;
      const compressionOptions = {
        maxSizeMB: 1.2,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
      };

      if (sourceImages.length > MAX_ALLOWABLE_IMAGES) {
        toast.warning(
          `Limiting upload to ${MAX_ALLOWABLE_IMAGES} images for performance.`
        );
      }

      const limitedImages = sourceImages.slice(0, MAX_ALLOWABLE_IMAGES);
      const imagesToUpload = [];

      // Step 1: Prepare images (compress if needed, convert to File objects)
      for (let index = 0; index < limitedImages.length; index += 1) {
        const image = limitedImages[index];
        try {
          // If already a stored URL (IPFS/Pinata/Cloudinary), skip processing
          const isStoredUrl = (url) =>
            typeof url === "string" &&
            (url.startsWith("http") || url.startsWith("ipfs://")) &&
            (url.includes("ipfs") || url.includes("pinata") || url.includes("cloudinary.com"));
          
          if (typeof image === "string" && isStoredUrl(image)) {
            imagesToUpload.push({ ipfsUrl: image, skip: true });
            continue;
          }

          if (image?.data_url && isStoredUrl(image.data_url)) {
            imagesToUpload.push({ ipfsUrl: image.data_url, skip: true });
            continue;
          }

          // If image object has no file and data_url is a stored URL
          if (image && !image.file && image.data_url && isStoredUrl(image.data_url)) {
            imagesToUpload.push({ ipfsUrl: image.data_url, skip: true });
            continue;
          }

          let fileToUpload = null;

          // If image has a file, compress it
          if (image?.file instanceof File) {
            const compressedFile = await imageCompression(image.file, compressionOptions);
            fileToUpload = compressedFile.size <= image.file.size ? compressedFile : image.file;
          }
          // If image has data_url (base64), convert to File and compress
          else if (image?.data_url?.startsWith("data:")) {
            const tempFile = await imageCompression.getFilefromDataUrl(
              image.data_url,
              `mint-${index}.jpg`
            );
            const compressedFile = await imageCompression(tempFile, compressionOptions);
            fileToUpload = compressedFile.size <= tempFile.size ? compressedFile : tempFile;
          }
          // If image is a base64 string
          else if (typeof image === "string" && image.startsWith("data:")) {
            const tempFile = await imageCompression.getFilefromDataUrl(
              image,
              `mint-inline-${index}.jpg`
            );
            const compressedFile = await imageCompression(tempFile, compressionOptions);
            fileToUpload = compressedFile.size <= tempFile.size ? compressedFile : tempFile;
          }
          // If image has data_url but not base64 (shouldn't happen, but handle it)
          else if (image?.data_url) {
            // Try to convert to file
            try {
              const tempFile = await imageCompression.getFilefromDataUrl(
                image.data_url,
                `mint-fallback-${index}.jpg`
              );
              fileToUpload = await imageCompression(tempFile, compressionOptions);
            } catch (e) {
              console.warn(`Skipping image ${index}: Could not process`, e);
              continue;
            }
          }

          if (fileToUpload) {
            imagesToUpload.push({ file: fileToUpload, skip: false });
          }
        } catch (compressionError) {
          console.error(`Image ${index} compression error:`, compressionError);
          // Skip this image if compression fails
        }
      }

      // Step 2: Upload to Pinata (only files that need uploading)
      const imagesForPinata = imagesToUpload
        .filter(img => !img.skip)
        .map(img => ({ file: img.file, data_url: null }));

      const alreadyUploadedCount = imagesToUpload.filter(img => img.skip).length;
      
      if (imagesForPinata.length === 0) {
        // All images are already IPFS URLs
        console.log(`✅ All ${alreadyUploadedCount} images are already IPFS URLs, skipping upload`);
        return imagesToUpload.map(img => img.ipfsUrl).filter(Boolean);
      }

      console.log(`📤 Uploading ${imagesForPinata.length} new images to Pinata (${alreadyUploadedCount} already uploaded)`);
      
      // Upload to Pinata
      const ipfsUrls = await pinImagesToIPFS(
        imagesForPinata,
        process.env.REACT_APP_PINATA_JWT_2,
        process.env.REACT_APP_PINATA_GATEWAY_2
      );

      // Step 3: Combine IPFS URLs with existing URLs
      const result = [];
      let ipfsIndex = 0;
      for (const img of imagesToUpload) {
        if (img.skip) {
          result.push(img.ipfsUrl);
        } else {
          if (ipfsIndex < ipfsUrls.length) {
            result.push(ipfsUrls[ipfsIndex]);
            ipfsIndex++;
          }
        }
      }

      return result.filter(Boolean);
    },
    []
  );

  // Navigation handlers - no auto-save, only manual save via "Save & Exit" button
  const handleNext = useCallback(() => {
    if (pageIdx < 5) {
      setPageIdx(pageIdx + 1);
    }
  }, [pageIdx]);

  const handleBack = useCallback(() => {
    if (pageIdx > 0) {
      setPageIdx(pageIdx - 1);
    }
  }, [pageIdx]);

  const handleSkip = useCallback(() => {
    handleNext();
  }, [handleNext]);

  // Image handlers - memoized to prevent unnecessary re-renders
  const removeElement = useCallback((array, index) => {
    const newImages = array.filter((_, i) => i !== index);
    setImages(newImages);
  }, [setImages]);

  const swapElements = useCallback((array, index1, index2) => {
    if (index1 === index2) return;
    const newImages = [...array];
    [newImages[index1], newImages[index2]] = [newImages[index2], newImages[index1]];
    setImages(newImages);
  }, [setImages]);

  // Typing detection refs
  const isUserTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const lastAddressInputRef = useRef('');
  const isLoadingAddressRef = useRef(false);

  // Address handling functions - memoized
  const getAddressList = useCallback(async (input) => {
    if (!service) return;
    service.getPlacePredictions(
      { input: input },
      function (predictions, status) {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          return;
        }
        setPredictList(predictions);
      }
    );
  }, [service, setPredictList]);

  const handleSelectFromAddressList = useCallback(async (place_id) => {
    if (!geocoder) return;
    isLoadingAddressRef.current = true; // Flag to prevent autocomplete trigger
    setIsOpen(false);
    const res = await geocoder.geocode({ placeId: place_id });
    
    // Update both pin location and address input
    setPinLocation({
      lat: res.results[0].geometry.location.lat(),
      lng: res.results[0].geometry.location.lng(),
    });
    
    // Update the input field with the selected address
    const formattedAddress = res.results[0].formatted_address;
    setAddressInput(formattedAddress);
    lastAddressInputRef.current = formattedAddress; // Store to prevent re-fetch
    
    // Parse address components
    const addressComponents = res?.results[0].address_components;
    let parsedAddress = {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
      string: "",
    };
    addressComponents?.forEach((component) => {
      if (component.types.includes("route")) {
        parsedAddress.street = component.long_name;
      } else if (component.types.includes("locality")) {
        parsedAddress.city = component.long_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        parsedAddress.state = component.short_name;
      } else if (component.types.includes("postal_code")) {
        parsedAddress.zipCode = component.long_name;
      } else if (component.types.includes("administrative_area_level_2")) {
        parsedAddress.county = component.long_name;
      }
    });
    setAddressDetails(parsedAddress);
    
    // Clear the prediction list
    setPredictList([]);
    
    setTimeout(() => {
      isLoadingAddressRef.current = false; // Reset flag after state updates
    }, 500);
  }, [geocoder, setIsOpen, setPinLocation, setAddressInput, setAddressDetails, setPredictList]);

  const handleSelectMapPin = useCallback(async (pinLocation) => {
    if (!geocoder) return;
    isLoadingAddressRef.current = true; // Flag to prevent autocomplete trigger
    setIsOpen(false);
    const res = await geocoder.geocode({ location: pinLocation });
    const formattedAddress = res.results[0].formatted_address;
    setAddressInput(formattedAddress);
    lastAddressInputRef.current = formattedAddress; // Store to prevent re-fetch
    
    const addressComponents = res?.results[0].address_components;
    let parsedAddress = {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
      string: "",
    };
    addressComponents?.forEach((component) => {
      if (component.types.includes("route")) {
        parsedAddress.street = component.long_name;
      } else if (component.types.includes("locality")) {
        parsedAddress.city = component.long_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        parsedAddress.state = component.short_name;
      } else if (component.types.includes("postal_code")) {
        parsedAddress.zipCode = component.long_name;
      } else if (component.types.includes("administrative_area_level_2")) {
        parsedAddress.county = component.long_name;
      }
    });
    setAddressDetails(parsedAddress);
    
    setTimeout(() => {
      isLoadingAddressRef.current = false; // Reset flag after state updates
    }, 500);
  }, [geocoder, setIsOpen, setAddressInput, setAddressDetails]);
  
  // Draft handling functions
  const handleSaveDraft = async (showToast = false, redirectToDashboard = false) => {
    try {
      if (!account) {
        console.warn('No wallet connected, skipping draft save');
        return;
      }
      
      const draftData = {
        metaDetails: {
          ...metaDetails,
          images: images,
          location: pinLocation,
          address: addressDetails,
          addressString: addressInput,
        },
        short: {
          price_per_day: 0,
          islisted: false,
          auto_approve: false
        }
      };
      
      // Don't upload images during draft save - only save image objects
      // Images will be uploaded once during the actual mint process
      const shouldUploadImages = false;
      
      const response = await DraftService.saveDraftWithImages(
        account,
        draftId,
        draftData,
        pageIdx,
        shouldUploadImages
      );
      
      if (response.success) {
        // Update draft ID if this was a new draft
        if (!draftId && response.property_id) {
          setDraftId(response.property_id);
        }
        
        setLastSavedAt(new Date());
        
        if (showToast) {
          toast.success('Draft saved successfully!');
        }
        
        // Redirect to dashboard if requested
        if (redirectToDashboard) {
          setTimeout(() => {
            navigate('/dashboard/host/dashboard');
          }, 1000); // Short delay to show success toast
        }
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      if (showToast) {
        toast.error('Failed to save draft');
      }
    }
  };
  
  const loadDraftData = async (propertyId) => {
    try {
      setIsLoadingDraft(true);
      isLoadingAddressRef.current = true; // Prevent autocomplete during load
      
      const draft = await DraftService.loadDraft(propertyId);
      
      if (draft && draft.draftData) {
        const parsedData = DraftService.parseDraftForForm(draft);
        
        if (parsedData) {
          // Load metadata
          if (parsedData.metaDetails) {
            setMetaDetails(prev => ({
              ...prev,
              ...parsedData.metaDetails
            }));
          }
          
          // Load images - convert IPFS URLs (strings) to format compatible with image components
          if (parsedData.images && parsedData.images.length > 0) {
            const processedImages = parsedData.images.map(img => {
              // If it's already an object with data_url, keep it as is
              if (img && typeof img === 'object' && img.data_url) {
                return img;
              }
              // If it's a string (IPFS URL), convert to object format for compatibility
              if (typeof img === 'string') {
                return {
                  data_url: img,
                  file: null, // No file object for IPFS images
                };
              }
              return img;
            });
            setImages(processedImages);
          }
          
          // Load address data
          if (parsedData.addressInput) {
            setAddressInput(parsedData.addressInput);
            lastAddressInputRef.current = parsedData.addressInput; // Store to prevent re-fetch
          }
          
          if (parsedData.addressDetails) {
            setAddressDetails(parsedData.addressDetails);
          }
          
          // Load pin location
          if (parsedData.pinLocation) {
            setPinLocation(parsedData.pinLocation);
          }
          
          // Set page to the saved step
          if (typeof parsedData.currentStep === 'number') {
            setPageIdx(parsedData.currentStep);
          }
          
          // Set draft ID
          if (parsedData.propertyId) {
            setDraftId(parsedData.propertyId);
          }
          
          setIsDraftLoaded(true);
          toast.success('Draft loaded successfully!');
        }
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Failed to load draft');
    } finally {
      setIsLoadingDraft(false);
      setTimeout(() => {
        isLoadingAddressRef.current = false; // Reset after everything is loaded
      }, 1000);
    }
  };

  // Minting handler
  const handleMint = async () => {
    try {
      if (!account || !wallet) {
        toast.error("Please connect your wallet first");
        return;
      }

      // Check verification before minting
      if (profile) {
        const hostVerification = getRoleSpecificVerification(profile, 'host');
        if (!hostVerification.isVerified) {
          toast.warning("Please verify your account with Emirates ID to mint properties", {
            autoClose: 4000,
          });
          navigate("/dashboard/profile?mode=host");
          return;
        }
      }

      if (images.length < 5) {
        toast.error("Please upload at least 5 photos of your property");
        return;
      }

      if (!metaDetails.buildingName || !metaDetails.description) {
        toast.error("Please fill in building name and description");
        return;
      }

      if (!addressInput || !addressDetails) {
        toast.error("Please select a valid address");
        return;
      }

      if (!pinLocation) {
        toast.error("Please select a location on the map");
        return;
      }

      if (mintJob && mintJob.sync_status === "pending") {
        toast.info("Mint job already queued. Please wait for confirmation.");
        return;
      }

      setIsMintSubmitting(true);
      const uploadToastId = toast.loading("Preparing images...");

      const mediaPayload = await prepareMediaForMint(images);

      if (!mediaPayload || mediaPayload.length < 5) {
        toast.update(uploadToastId, {
          render:
            "Failed to prepare images. Please ensure you have at least 5 photos and try again.",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        setIsMintSubmitting(false);
        return;
      }

      toast.update(uploadToastId, {
        render: "Creating property record...",
        isLoading: true,
      });

      const { images: _removedImages, ...metadataWithoutImages } =
        metaDetails || {};

      const payload = {
        walletAddress: account,
        propertyId: metaDetails.propertyId || generateRandom8DigitInteger().toString(),
        metadata: {
          ...metadataWithoutImages,
          location: pinLocation,
          address: addressDetails,
          addressString: addressInput,
        },
        pricing: {
          price_per_day: 0,
          islisted: false,
          auto_approve: false,
        },
        media: mediaPayload,
      };

      let propertyResponse;
      try {
        // Step 1: Create property record in backend (pending status)
        propertyResponse = await mintProperty(payload);
        const safePropertyId = propertyResponse?.propertyId || metaDetails.propertyId;

        toast.update(uploadToastId, {
          render: "Please approve the transaction in your wallet...",
          isLoading: true,
        });

        // Step 2: Sign and broadcast transaction using user's wallet
        const mainnet = Mainnet();
        const chainId = mainnet.chainId;
        const endpt = mainnet.endptTm;
        const walletEx = wallet || "keplr";
        const contractAddr = process.env.REACT_APP_RENTAL_SMART_CONTRACT;

        const metadataCid = propertyResponse?.metadataCid;
        if (!metadataCid) {
          throw new Error("Failed to get metadata CID from backend response");
        }

        const isEvmWallet = wallet === "metamask" || wallet === "bybitWallet";
        // Wasm rental contract expects owner as bech32 (nibi1...); EVM sends 0x... so we must convert
        const ownerForMint = isEvmWallet
          ? await evmToBech32Address(account)
          : account;

        const contractMessage = {
          mint: {
            token_id: safePropertyId,
            owner: ownerForMint,
            token_uri: metadataCid ? `ipfs://${metadataCid}` : "",
            extension: {},
          },
        };

        let txHash;

        if (isEvmWallet) {
          // EVM path: MetaMask / other EVM wallets via CodedEstateEvmInterface
          txHash = await executeRentalWithWallet({
            emailData: null,
            funds: [{ denom: "unibi", amount: "500000" }],
            currentTime: new Date().toISOString(),
            chainId,
            endpt,
            contractAddr,
            dispatch,
            tokenId: safePropertyId,
            receiver: account,
            CONTRACT_MESSAGE: contractMessage,
            account,
            walletType: wallet,
            walletEx,
            tokenToSend: null,
          });
          if (!txHash) throw new Error("EVM mint transaction failed or was rejected.");
        } else {
          // Cosmos path: Leap / Keplr
          if (!window[walletEx]) {
            throw new Error(`${walletEx} wallet not found. Please install and connect your wallet.`);
          }
          await window[walletEx].enable(chainId);
          const signer = await window[walletEx].getOfflineSigner(chainId);
          const signingClient = await NibiruTxClient.connectWithSigner(endpt, signer);
          const tx = await signingClient.wasmClient.executeMultiple(
            account,
            [
              {
                contractAddress: contractAddr,
                msg: contractMessage,
              },
            ],
            {
              amount: [{ denom: "unibi", amount: "500000" }],
              gas: "800000",
            }
          );
          txHash = tx.transactionHash;
        }

        toast.update(uploadToastId, {
          render: "Transaction confirmed! Updating property status...",
          isLoading: true,
        });

        // Step 3: Send txHash to backend to update property status
        try {
          await api("property/mint/confirm", {
            propertyId: safePropertyId,
            txHash,
            walletAddress: account,
          });
        } catch (confirmError) {
          console.warn("Failed to confirm mint in backend:", confirmError);
          // Don't fail the whole flow if backend confirmation fails
        }

        dispatch(
          upsertMintJob({
            ...propertyResponse,
            propertyId: safePropertyId,
            sync_status: "confirmed",
            txHash,
          })
        );

        // Step 4: Block dates from Airbnb if they were imported
        if (airbnbBlockedDates && airbnbBlockedDates.url) {
          try {
            console.log(`📅 [Mint] Blocking ${airbnbBlockedDates.totalBlockedDates} dates from Airbnb import...`);
            toast.update(uploadToastId, {
              render: "Blocking dates from Airbnb calendar...",
              isLoading: true,
            });
            
            const blockDatesResponse = await IcalSyncService.scrapeListingCalendar(
              airbnbBlockedDates.url,
              safePropertyId
            );
            
            if (blockDatesResponse.success) {
              console.log(`✅ [Mint] Successfully blocked ${blockDatesResponse.totalBlockedDates || 0} dates from Airbnb`);
              // Clear blocked dates state after successful blocking
              setAirbnbBlockedDates(null);
            } else {
              console.warn(`⚠️ [Mint] Failed to block dates from Airbnb:`, blockDatesResponse.error);
            }
          } catch (blockDatesError) {
            console.error("❌ [Mint] Error blocking dates from Airbnb:", blockDatesError);
            // Don't fail the whole mint process if blocking dates fails
          }
        }

        toast.update(uploadToastId, {
          render: "🎉 Property minted successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });

        // Navigate to property page or dashboard after a short delay
        setTimeout(() => {
          navigate(`/dashboard/host/nfts`);
        }, 2000);
      } catch (apiError) {
        console.error("Mint error:", apiError);
        const errorMessage =
          apiError?.response?.data?.error || apiError.message || "Unknown error";

        toast.update(uploadToastId, {
          render: `Mint failed: ${errorMessage}`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });

        // If property was created but transaction failed, update status
        if (propertyResponse?.propertyId) {
          try {
            await api("property/mint/fail", {
              propertyId: propertyResponse.propertyId,
              walletAddress: account,
              error: errorMessage,
            });
          } catch (failError) {
            console.warn("Failed to update property status:", failError);
          }
        }
      }
    } catch (error) {
      console.error("Minting error:", error);
      toast.error("Failed to submit mint request. Please try again.");
    } finally {
      setIsMintSubmitting(false);
    }
  };

  // Debounced address autocomplete to prevent excessive API calls
  useEffect(() => {
    if (
      !addressInput || 
      addressInput.length < 3 || 
      addressInput === lastAddressInputRef.current ||
      isLoadingAddressRef.current
    ) {
      return;
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout for debounced call
    typingTimeoutRef.current = setTimeout(() => {
      if (addressInput && addressInput.length >= 3 && !isLoadingAddressRef.current) {
        getAddressList(addressInput);
        lastAddressInputRef.current = addressInput;
      }
    }, 300); // 300ms debounce

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [addressInput, getAddressList]);
  
  // Check verification on mount and redirect if not verified
  useEffect(() => {
    if (account && profile) {
      const hostVerification = getRoleSpecificVerification(profile, 'host');
      if (!hostVerification.isVerified) {
        toast.warning("Please verify your account with Emirates ID to mint properties", {
          autoClose: 4000,
        });
        navigate("/dashboard/profile?mode=host");
      }
    }
  }, [account, profile, navigate]);

  // Load draft on mount if draft ID is in URL
  useEffect(() => {
    const draftIdParam = searchParams.get('draft');
    if (draftIdParam && !isDraftLoaded) {
      // Check verification before loading draft
      if (account && profile) {
        const hostVerification = getRoleSpecificVerification(profile, 'host');
        if (!hostVerification.isVerified) {
          toast.warning("Please verify your account with Emirates ID to access drafts", {
            autoClose: 4000,
          });
          navigate("/dashboard/profile?mode=host");
          return;
        }
      }
      loadDraftData(draftIdParam);
    }
  }, [searchParams, isDraftLoaded, account, profile, navigate]);

  // Load Airbnb imported data on mount
  useEffect(() => {
    const isAirbnbImport = searchParams.get('airbnb') === 'true';
    if (isAirbnbImport) {
      // Check verification before loading Airbnb data
      if (account && profile) {
        const hostVerification = getRoleSpecificVerification(profile, 'host');
        if (!hostVerification.isVerified) {
          toast.warning("Please verify your account with Emirates ID to import from Airbnb", {
            autoClose: 4000,
          });
          navigate("/dashboard/profile?mode=host");
          return;
        }
      }
      const importedData = sessionStorage.getItem('airbnbImportedData');
      if (importedData) {
        const loadAirbnbData = async () => {
          try {
            isLoadingAddressRef.current = true; // Prevent autocomplete during load
            setIsLoadingDraft(true); // Show loading state
            const data = JSON.parse(importedData);
            
            // Load metadata (including normalized amenities)
            const normalizedAmenities = normalizeAirbnbAmenities(
              data.amenitiesDetailed,
              data.metaDetails?.amenities
            );

            const metaPayload = data.metaDetails || {};
            setMetaDetails((prev) => ({
              ...prev,
              ...metaPayload,
              amenities: normalizedAmenities,
            }));
            
            // Convert image URLs to File objects
            if (data.images && data.images.length > 0) {
              toast.loading(`Converting ${data.images.length} images from Airbnb...`);
              try {
                const convertedImages = await convertImageUrlsToFiles(data.images);
                setImages(convertedImages);
                toast.dismiss();
                toast.success(`Successfully converted ${convertedImages.length} images!`);
              } catch (error) {
                console.error('Error converting images:', error);
                toast.dismiss();
                toast.error('Some images failed to convert. You may need to re-upload them.');
                // Still set images even if conversion failed partially
                setImages(data.images);
              }
            }
            
            // Geocode address via Google Maps API to get proper location and formatted address
            if (data.addressString && geocoder && window.google?.maps) {
              try {
                console.log("🗺️ [Mint] Geocoding Airbnb address via Google Maps API...");
                console.log("🗺️ [Mint] Address to geocode:", data.addressString);
                isLoadingAddressRef.current = true; // Prevent autocomplete during geocoding
                
                // Geocode the address string
                const geocodeResult = await new Promise((resolve, reject) => {
                  geocoder.geocode({ address: data.addressString }, (results, status) => {
                    if (status === 'OK' && results && results.length > 0) {
                      resolve(results[0]);
                    } else {
                      reject(new Error(`Geocoding failed: ${status}`));
                    }
                  });
                });
                
                // Extract location from geocoded result
                const location = geocodeResult.geometry.location;
                const geocodedLocation = {
                  lat: location.lat(),
                  lng: location.lng()
                };
                
                // Update pin location on map
                setPinLocation(geocodedLocation);
                console.log("✅ [Mint] Address geocoded successfully:", geocodedLocation);
                
                // Use Google's formatted address (more accurate)
                const formattedAddress = geocodeResult.formatted_address;
                setAddressInput(formattedAddress);
                lastAddressInputRef.current = formattedAddress;
                
                // Parse address components from Google's geocoded result
                const addressComponents = geocodeResult.address_components;
                let parsedAddress = {
                  street: "",
                  city: "",
                  state: "",
                  zipCode: "",
                  county: "",
                  string: formattedAddress,
                };
                
                addressComponents?.forEach((component) => {
                  if (component.types.includes("route")) {
                    parsedAddress.street = component.long_name;
                  } else if (component.types.includes("locality")) {
                    parsedAddress.city = component.long_name;
                  } else if (component.types.includes("administrative_area_level_1")) {
                    parsedAddress.state = component.short_name;
                  } else if (component.types.includes("postal_code")) {
                    parsedAddress.zipCode = component.long_name;
                  } else if (component.types.includes("administrative_area_level_2")) {
                    parsedAddress.county = component.long_name;
                  }
                });
                
                setAddressDetails(parsedAddress);
                console.log("✅ [Mint] Address details parsed:", parsedAddress);
                
                // Set zoom to show the location properly
                setZoom(15);
                
              } catch (geocodeError) {
                console.warn("⚠️ [Mint] Geocoding failed, using original data:", geocodeError);
                // Fallback to original data if geocoding fails
                if (data.location) {
                  setPinLocation(data.location);
                }
                if (data.addressString) {
                  setAddressInput(data.addressString);
                  lastAddressInputRef.current = data.addressString;
                }
                if (data.address) {
                  setAddressDetails(data.address);
                }
              } finally {
                setTimeout(() => {
                  isLoadingAddressRef.current = false; // Reset after geocoding
                }, 1000);
              }
            } else {
              // Fallback if no addressString or geocoder not available
              if (data.location) {
                setPinLocation(data.location);
              }
              if (data.addressString) {
                setAddressInput(data.addressString);
                lastAddressInputRef.current = data.addressString;
              }
              if (data.address) {
                setAddressDetails(data.address);
              }
            }
            
            // Load blocked dates (but don't display them - will be blocked after mint)
            if (data.blockedDates) {
              setAirbnbBlockedDates(data.blockedDates);
              console.log(`📅 [Mint] Loaded ${data.blockedDates.totalBlockedDates} blocked dates from Airbnb import (will be blocked after mint)`);
            }
            
        // Set page to last page (publish page)
        setPageIdx(5);
            
            // Clear the imported data from sessionStorage
            sessionStorage.removeItem('airbnbImportedData');
            
            // Remove airbnb param from URL
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('airbnb');
            navigate(`/dashboard/host/mint?${newSearchParams.toString()}`, { replace: true });
            
            toast.success('Property data imported from Airbnb! Please review and fill in any missing information.');
            
            setIsLoadingDraft(false);
            setTimeout(() => {
              isLoadingAddressRef.current = false; // Reset after everything is loaded
            }, 1000);
          } catch (error) {
            console.error('Error loading Airbnb imported data:', error);
            toast.error('Failed to load imported data');
            isLoadingAddressRef.current = false;
            setIsLoadingDraft(false);
          }
        };
        
        loadAirbnbData();
      }
    }
  }, [searchParams, navigate, geocoder]);
  

  // Render current page based on pageIdx - use conditional rendering instead of function
  // This prevents "Component is not a function" errors while maintaining performance
  const renderCurrentPage = () => {
    switch (pageIdx) {
      case 0:
        return (
          <MintWelcomePage
            onNext={handleNext}
            onSkip={handleSkip}
          />
        );
      case 1:
        return (
          <MintBasicInfoPage
            zoom={zoom}
            setZoom={setZoom}
            pinLocation={pinLocation}
            setPinLocation={setPinLocation}
            addressInput={addressInput}
            setAddressInput={setAddressInput}
            predictList={predictList}
            setPredictList={setPredictList}
            addressDetails={addressDetails}
            setAddressDetails={setAddressDetails}
            getAddressList={getAddressList}
            handleSelectFromAddressList={handleSelectFromAddressList}
            handleSelectMapPin={handleSelectMapPin}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            metaDetails={metaDetails}
            setMetaDetails={setMetaDetails}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <MintEssentialsPage
            metaDetails={metaDetails}
            setMetaDetails={setMetaDetails}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <MintAmenitiesPage
            metaDetails={metaDetails}
            setMetaDetails={setMetaDetails}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 4:
        return (
          <MintPhotosPage
            images={images}
            setImages={setImages}
            removeElement={removeElement}
            dragStartIdx={dragStartIdx}
            setDragStartIdx={setDragStartIdx}
            dragEndIdx={dragEndIdx}
            setDragEndIdx={setDragEndIdx}
            swapElements={swapElements}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 5:
        return (
          <MintPublishPage
            images={images}
            removeElement={removeElement}
            dragStartIdx={dragStartIdx}
            setDragStartIdx={setDragStartIdx}
            dragEndIdx={dragEndIdx}
            setDragEndIdx={setDragEndIdx}
            swapElements={swapElements}
            metaDetails={metaDetails}
            setMetaDetails={setMetaDetails}
            addressInput={addressInput}
            setAddressInput={setAddressInput}
            addressDetails={addressDetails}
            checkInModal={checkInModal}
            setCheckInModal={setCheckInModal}
            checkOutModal={checkOutModal}
            setCheckOutModal={setCheckOutModal}
            onBack={handleBack}
            onMint={handleMint}
            mintJob={mintJob}
            isMinting={isMintSubmitting}
          />
        );
      default:
        return <MintWelcomePage onNext={handleNext} onSkip={handleSkip} />;
    }
  };

  return (
    <div className="select-none w-full overflow-auto max-h-[calc(100vh-80px)] h-full p-[6px] sm:p-[10px]">
      <div className="w-full h-full space-y-[12px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col">
        {/* Draft Status Bar */}
        {pageIdx > 0 && (
          <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-3 shadow-sm flex justify-between items-center">
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#666666]">
                {draftId ? 'Draft ID:' : 'New Draft'}
              </span>
              {draftId && (
                <span className="text-xs font-mono bg-[#f0f0f0] px-2 py-1 rounded">
                  {draftId.substring(0, 12)}...
                </span>
              )}
              {lastSavedAt && (
                <span className="text-xs text-[#959595]">
                  Last saved: {lastSavedAt.toLocaleTimeString()}
                </span>
              )}
            </div>
            <PurpleButton
              text="Save & Exit"
              onClick={() => handleSaveDraft(true, true)}
            />
          </div>
        )}
        
        {/* Mint status banner */}
        {mintJob && mintStatusTone && (
          <div
            className={`border rounded-[10px] p-4 shadow-sm flex flex-col gap-2 ${mintStatusTone.bg} ${mintStatusTone.border} ${mintStatusTone.text}`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-sm font-medium">{mintStatusMessage}</span>
              {mintJob.txHash && (
                <a
                  href={`https://explorer.nibiru.fi/nibiru-testnet-2/tx/${mintJob.txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline"
                >
                  View transaction
                </a>
              )}
            </div>
            <div className="text-xs opacity-80">
              <span>Job ID: {mintJob.jobId || "N/A"}</span>
              {mintJob.metadataUrl && (
                <span className="ml-3">
                  Metadata:{" "}
                  <a
                    href={mintJob.metadataUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    View on IPFS
                  </a>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoadingDraft && (
          <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-6 shadow-sm text-center">
            <div className="text-[#666666]">Loading draft...</div>
          </div>
        )}
        
        {/* Page content - let individual pages handle their own layout */}
        {!isLoadingDraft && (
          <div className="flex-1">
            {renderCurrentPage()}
          </div>
        )}
      </div>
    </div>
  );
};

export default MintMain;
